import React, { FC, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import useToast from 'hooks/useToast';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
// mui components
import { Dialog, Grid, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import RadioController from 'widgets/selections/modals/components/custom-radio/RadioController';
import OutlinedInputController from 'controllers/outlined-input-controller';
import DatePickerController from 'controllers/date-picker-controller';
import StyledForm from 'components/styled-form';
// utils
import {
  calculateBusinessDays,
  handleCalculateEndDate,
  handleCalculateStartDateMinus,
  toIsoUtcDate,
} from 'utils/postDateHelper';
import { utcDateToZoned, zonedDateToUtc } from 'utils/postDateHelper';
import { TEMP_ITEM_ID_COEFFICIENT } from 'utils/constants';
import { ICompleteItemForm } from './types';
import { weekDays } from 'utils/helpers';
// api
import { useCompleteSchedulePhaseItemMutation } from 'redux-store/api/schedule-phases-items';
import { IGanttUpdatePhaseItem, IPhaseItem, PhaseStatusesEnum } from 'redux-store/types';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import { useSyncSelectedProject } from 'hooks/useSyncSelectedProject';

import {
  COMPLETE_TYPES_OPTIONS,
  PhaseItemCompletionStrategy,
} from 'utils/constants/complete-types';
// icons
import WarningIcon from 'assets/icons/new/selection/warning-icon.svg';
// styles
import { styles } from './styles';
import {
  getPhaseItemsStartEndDates,
  PhaseItemDateRange,
} from 'redux-store/api/projects/project.utils';

interface OwnProps {
  open: boolean;
  itemId: number | null;
  itemTitle: string;
  itemStartDate: string;
  projectId: number;
  workingDays: number[];
  selectedItem: IPhaseItem;
  fromEditModal?: boolean;
  gantt?: boolean;
  ganttItem?: IGanttUpdatePhaseItem;
  closeModal: () => void;
  handleCloseItemModal?: () => void;
  handleAllowUpdateProject?: () => void;
  handleSetScrollingId?: (id: string | number) => void;
  handleUpdateTask?: (taskId: string, task?: IGanttUpdatePhaseItem) => void;
  updateGanttChart?: (updatedTaskId?: string | number) => void;
}

const CompleteItemModal: FC<OwnProps> = ({
  open,
  itemId,
  itemTitle,
  itemStartDate,
  workingDays,
  selectedItem,
  projectId,
  fromEditModal,
  gantt,
  ganttItem,
  closeModal,
  handleCloseItemModal,
  handleSetScrollingId,
  handleAllowUpdateProject,
  handleUpdateTask,
  updateGanttChart,
}) => {
  const { project } = useSyncSelectedProject(projectId);

  const [completePhaseItem, { isLoading: loading }] = useCompleteSchedulePhaseItemMutation();
  const nonWorkingDays = weekDays.filter((x: any) => !project?.workingDays?.includes(x));

  const isOverdue = selectedItem?.status === 'OVERDUE';
  const dayjsStartDate = dayjs(selectedItem.startDate);
  const dayjsEndDate = dayjs(selectedItem.endDate);
  const dayjsToday = dayjs();
  const isEarlyComplete = dayjsToday.isBefore(dayjsEndDate);

  const showToast = useToast();
  const dispatch = useAppDispatch();

  const form = useForm<ICompleteItemForm>({
    defaultValues: {
      completionStrategy: PhaseItemCompletionStrategy.keepDates,
      successors: selectedItem?.linkedItems,
      startDate: utcDateToZoned(selectedItem?.startDate),
      endDate: utcDateToZoned(selectedItem?.endDate),
      workDays: selectedItem?.workDays,
    },
  });

  const completeType = useWatch({
    control: form.control,
    name: 'completionStrategy',
  });

  const dateRangesById = useMemo(() => {
    return getPhaseItemsStartEndDates(project?.phases);
  }, [project]);

  const onSubmit = async (formValues) => {
    if (itemId) {
      completePhaseItem({
        startDate: zonedDateToUtc(formValues.startDate),
        endDate: zonedDateToUtc(formValues.endDate),
        workDays: formValues.workDays,
        title: selectedItem?.title,
        phaseId: selectedItem?.phaseId,
        id: itemId,
        linkedItems: [],
        completionStrategy: formValues.completionStrategy,
      })
        .unwrap()
        .then(() => {
          closeModal();

          if (fromEditModal) {
            updateGanttChart?.();

            handleCloseItemModal();
          }

          if (!fromEditModal && gantt) {
            handleUpdateTask(String(itemId * TEMP_ITEM_ID_COEFFICIENT), {
              id: itemId * TEMP_ITEM_ID_COEFFICIENT,
              text: ganttItem.text,
              start_date: ganttItem?.start_date,
              end_date: ganttItem?.end_date,
              parent: ganttItem.parent,
              constraint_type: 'snet',
              constraint_date: ganttItem?.constraint_date,
              item_status: PhaseStatusesEnum?.COMPLETED,
              ownStartDate: ganttItem?.ownStartDate,
              ownEndDate: ganttItem?.ownEndDate,
              ownPredecessors: ganttItem?.ownPredecessors,
              ownLinkedItems: ganttItem?.ownLinkedItems,
            });
          }
        })
        .catch((err) => {
          showToast({
            type: 'error',
            message:
              typeof err?.data?.message === 'string'
                ? err?.data?.message
                : 'Something went wrong. Please try again or contact support for help.',
          });
        })
        .finally(() => {
          if (!!handleAllowUpdateProject) {
            handleAllowUpdateProject();
          }
        });
    }
  };

  const shouldDisableDate = (date: Date) => {
    const day = date.getDay();
    return nonWorkingDays.includes(day);
  };

  const warningMessage = () => {
    if (isOverdue || isEarlyComplete) {
      return (
        <Grid sx={styles.warningMessageContainer}>
          <Grid sx={styles.warningMessage}>
            <Grid sx={styles.warningIconContainer}>
              <WarningIcon />
            </Grid>

            <Grid sx={styles.textsContainer}>
              {isOverdue && (
                <Typography>This item is overdue and may affect linked tasks.</Typography>
              )}

              {isEarlyComplete && (
                <Typography>
                  This item is being completed ahead of schedule and may affect dependencies.
                </Typography>
              )}

              <Typography>How would you like to proceed?</Typography>
            </Grid>
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const handleChangeCompletionStrategy = (event) => {
    form.setValue('completionStrategy', event.target.value);

    if (
      event.target.value === PhaseItemCompletionStrategy.keepDates ||
      event.target.value === PhaseItemCompletionStrategy.updateEndDate
    ) {
      form.setValue('startDate', utcDateToZoned(selectedItem.startDate));
      form.setValue('endDate', utcDateToZoned(selectedItem.endDate));
      form.setValue('workDays', selectedItem.workDays);
    }

    if (event.target.value === PhaseItemCompletionStrategy.updateDate) {
      if (isOverdue || isEarlyComplete) {
        const startIsAfter = dayjsStartDate.isAfter(dayjsToday);

        if (startIsAfter) {
          form.setValue('startDate', utcDateToZoned(dayjs.utc().startOf('day').toString()));
        }

        if (!startIsAfter) {
          form.setValue('startDate', utcDateToZoned(selectedItem.startDate));
        }

        const formWorkDays = calculateBusinessDays(
          selectedItem.startDate,
          toIsoUtcDate(new Date()),
          workingDays
        );

        form.setValue('endDate', utcDateToZoned(toIsoUtcDate(new Date())));
        form.setValue('workDays', formWorkDays);
      }
    }
  };

  const workDaysChangeHandler = (e) => {
    const formStartDate = form.getValues('startDate');

    const isoStart = toIsoUtcDate(formStartDate);
    const newEndDate = handleCalculateEndDate(isoStart, e.target.value, workingDays);

    form.setValue('workDays', e.target.value);
    form.setValue('endDate', newEndDate);
  };

  const endDateUpdateHandler = (newEndDateUtc: string) => {
    form.setValue('endDate', utcDateToZoned(newEndDateUtc));

    const workdayAdjustedStartDate = zonedDateToUtc(
      handleCalculateEndDate(selectedItem.startDate, selectedItem.workDays, workingDays)
    );

    const newStartDate =
      newEndDateUtc < workdayAdjustedStartDate
        ? (handleCalculateStartDateMinus(
            newEndDateUtc,
            selectedItem.workDays,
            workingDays
          ) as dayjs.Dayjs)
        : utcDateToZoned(selectedItem.startDate);

    form.setValue('startDate', newStartDate);

    // clear error if exists
    if (form.getFieldState('endDate')?.error) {
      form.clearErrors('endDate');
    }

    const updatedWorkDays = calculateBusinessDays(
      toIsoUtcDate(form.getValues('startDate')),
      newEndDateUtc,
      workingDays
    );

    form.setValue('workDays', updatedWorkDays);
  };

  const endDateChangeHandler = useCallback((e) => {
    const end = utcDateToZoned(e);
    form.setValue('endDate', end);

    const formStartDate = form.getValues('startDate');
    const formWorkDays = calculateBusinessDays(toIsoUtcDate(formStartDate), e, workingDays);
    const endDateState = form.getFieldState('endDate');

    if (!!endDateState?.error) {
      form.setError('endDate', null);
    }

    form.setValue('workDays', formWorkDays);
  }, []);

  const startDateChangeHandler = useCallback((e) => {
    const start = utcDateToZoned(e);
    form.setValue('startDate', start);

    const currentWorkDays = form.getValues('workDays');
    const newEndDate = handleCalculateEndDate(e, currentWorkDays, workingDays);

    form.setValue('endDate', newEndDate);
  }, []);

  const getDatePickerProps = (
    selectedItem: IPhaseItem,
    dateRangesById: Record<number, PhaseItemDateRange>
  ) => {
    if (!selectedItem?.predecessors?.length) return {};

    const predecessors = selectedItem.predecessors.map((pred) => ({
      ...pred,
      startDate: dateRangesById?.[pred.predecessorId]?.startDate,
      endDate: dateRangesById?.[pred.predecessorId]?.endDate,
    }));

    const latestPredecessor = predecessors.reduce((latest, current) => {
      if (!latest) return current;
      return new Date(current.startDate) > new Date(latest.startDate) ? current : latest;
    }, null);

    const minDate = handleCalculateEndDate(
      latestPredecessor.startDate,
      selectedItem.workDays,
      workingDays
    );

    return {
      minDate,
    };
  };

  const renderOptionContent = () => {
    if (completeType === PhaseItemCompletionStrategy.updateDate) {
      return (
        <Grid sx={styles.successorsContentContainer}>
          <Grid sx={styles.successorsContainer}>
            <Typography sx={styles.successorsTitle}>
              Specify the new start and end date for this schedule item.
            </Typography>

            <Grid sx={styles.successorsTableContainer}>
              <Grid sx={styles.successorsTableHead}>
                <Grid className="tableHeadItem">Start Date</Grid>
                <Grid className="tableHeadItem">Work Days</Grid>
                <Grid className="tableHeadItem">End Date</Grid>
              </Grid>

              <Grid sx={styles.row}>
                <Grid className="rowItem">
                  <DatePickerController
                    icon
                    required
                    withError
                    fullWidth
                    name="startDate"
                    shouldDisableDate={shouldDisableDate}
                    additionalOnChange={startDateChangeHandler}
                  />
                </Grid>

                <Grid className="rowItem">
                  <OutlinedInputController
                    required
                    fullWidth
                    withError
                    name="workDays"
                    type="number"
                    inputProps={{ min: 1 }}
                    additionalOnChange={workDaysChangeHandler}
                  />
                </Grid>
                <Grid className="rowItem">
                  <DatePickerController
                    required
                    icon
                    withError
                    fullWidth
                    name="endDate"
                    minDate={utcDateToZoned(selectedItem.startDate)}
                    shouldDisableDate={shouldDisableDate}
                    defaultCalendarMonth={new Date()}
                    additionalOnChange={endDateChangeHandler}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }

    if (completeType === PhaseItemCompletionStrategy.updateEndDate) {
      const datePickerProps = getDatePickerProps(selectedItem, dateRangesById);

      return (
        <Grid sx={styles.successorsContentContainer}>
          <Grid sx={styles.successorsContainer}>
            <Typography sx={styles.successorsTitle}>
              Specify the new end date for this schedule item.
            </Typography>

            <Grid sx={styles.successorsTableContainer}>
              <Grid sx={styles.successorsTableHead}>
                <Grid className="tableHeadItem">End Date</Grid>
              </Grid>

              <Grid sx={styles.row}>
                <Grid sx={styles.fullRowItem}>
                  <DatePickerController
                    required
                    icon
                    withError
                    fullWidth
                    name="endDate"
                    shouldDisableDate={shouldDisableDate}
                    defaultCalendarMonth={new Date()}
                    additionalOnChange={endDateUpdateHandler}
                    {...datePickerProps}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} sx={styles.modal} onClose={closeModal}>
      <FormProvider {...form}>
        <StyledForm onSubmit={form.handleSubmit(onSubmit)}>
          <Grid sx={styles.titleContainer}>
            <Typography sx={styles.title}>Confirm Item Completion</Typography>

            <Typography sx={styles.subtitle}>
              Select and confirm the completion date for this schedule item.
            </Typography>
          </Grid>

          <Grid sx={styles.nameContainer}>
            <Typography sx={styles.itemNameTitle}>Item Name</Typography>
            <Typography sx={styles.itemName}>{itemTitle}</Typography>
          </Grid>

          {warningMessage()}

          {(isOverdue || isEarlyComplete) && (
            <Grid sx={styles.radioButtonsContainer}>
              <RadioController
                handleChange={handleChangeCompletionStrategy}
                name="completionStrategy"
                options={COMPLETE_TYPES_OPTIONS}
                customStyles={styles.radioButtons}
                radioGroupProps={{
                  row: false,
                }}
              />
            </Grid>
          )}

          {renderOptionContent()}

          <Grid sx={styles.buttonsContainer}>
            <LoadingButton
              loading={loading}
              variant="outlined"
              onClick={closeModal}
              sx={styles.cancelButton}
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              type="submit"
              loading={loading}
              variant="contained"
              sx={styles.saveButton}
            >
              Confirm
            </LoadingButton>
          </Grid>
        </StyledForm>
      </FormProvider>
    </Dialog>
  );
};

export default CompleteItemModal;
